/**
 * Level select.
 *
 * SHELL-030 to SHELL-036 are mostly still open: this renders every level in
 * manifest order (SHELL-030), which the validated index gives for free, but
 * not lock state or stars (SHELL-031, SHELL-032) -- those need the save layer
 * Sprint 2 adds, and fabricating them now is exactly the "looks more finished
 * than it is" failure CLAUDE.md warns about.
 *
 * The unknown-game branch (SHELL-013) stays a plain check with no hook calls
 * before it, so `gameId` can change across renders of the same route without
 * breaking the rules of hooks -- the hook-using part of this screen lives in
 * `LevelSelectLoaded`, a separate component, for exactly that reason.
 */

import { Screen } from '@shell/Screen';
import { routes } from '@shell/routes';
import { isKnownGame } from '@platform/game-registry';
import { NotFound } from '@shell/screens/NotFound';
import { loadFailureMessage } from '@shell/load-failure-message';
import { useGameModule } from '@shell/use-game-module';

export function LevelSelect({ gameId }: { gameId: string }) {
  if (!isKnownGame(gameId)) return <NotFound />;
  return <LevelSelectLoaded gameId={gameId} />;
}

function LevelSelectLoaded({ gameId }: { gameId: string }) {
  const state = useGameModule(gameId);

  if (state.status === 'loading') {
    return (
      <Screen title="Levels">
        <p>Loading&hellip;</p>
      </Screen>
    );
  }

  if (state.status === 'error') {
    // DATA-045 / SHELL-024: a registered game whose chunk or index would not
    // load gets an error and a retry, not the not-found screen SHELL-013
    // renders for a game that was never registered. Different causes, kept
    // visibly different.
    return (
      <Screen title="Levels">
        <p>{loadFailureMessage(state.failure)}</p>
        <nav class="screen-nav">
          <button type="button" onClick={state.retry}>
            Try again
          </button>
          <a class="nav-link" href={routes.picker()}>
            Back to the game picker
          </a>
        </nav>
      </Screen>
    );
  }

  return (
    <Screen title="Levels">
      <ul class="level-list">
        {state.index.levels.map((entry) => (
          <li key={entry.id}>
            <a class="nav-link" href={routes.play(gameId, entry.id)}>
              {entry.title}
            </a>
          </li>
        ))}
      </ul>
      <nav class="screen-nav">
        <a class="nav-link" href={routes.picker()}>
          Back to the game picker
        </a>
      </nav>
    </Screen>
  );
}
